import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const CustomCursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [outerPosition, setOuterPosition] = useState({ x: 0, y: 0 });
  const [isPointer, setIsPointer] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });

      const target = e.target as HTMLElement;
      setIsPointer(
        window.getComputedStyle(target).cursor === "pointer" ||
        target.tagName === "BUTTON" ||
        target.tagName === "A" ||
        target.closest("button") !== null ||
        target.closest("a") !== null
      );
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Smooth follow effect for outer ring with delay
  useEffect(() => {
    const followMouse = () => {
      setOuterPosition((prev) => ({
        x: prev.x + (position.x - prev.x) * 0.08,
        y: prev.y + (position.y - prev.y) * 0.08,
      }));
    };

    const animationFrame = requestAnimationFrame(followMouse);
    return () => cancelAnimationFrame(animationFrame);
  }, [position, outerPosition]);

  // Don't render custom cursor on mobile/touch devices
  if (isMobile) return null;

  return (
    <>
      {/* Main cursor dot */}
      <div
        className="fixed pointer-events-none z-[9999] mix-blend-screen transition-transform duration-100 ease-out"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) scale(${isPointer ? 1.5 : 1})`,
        }}
      >
        <div className="w-3 h-3 rounded-full bg-gradient-to-r from-[hsl(var(--holographic-cyan))] to-[hsl(var(--holographic-pink))]" />
      </div>

      {/* Outer ring with smooth follow */}
      <div
        className="fixed pointer-events-none z-[9998] mix-blend-screen transition-transform duration-300 ease-out"
        style={{
          left: `${outerPosition.x}px`,
          top: `${outerPosition.y}px`,
          transform: `translate(-50%, -50%) scale(${isPointer ? 1.8 : 1})`,
        }}
      >
        <div className="w-8 h-8 rounded-full border-2 border-[hsl(var(--holographic-cyan))]/50 animate-pulse" />
      </div>

      {/* Glow effect */}
      <div
        className="fixed pointer-events-none z-[9997] transition-all duration-500 ease-out"
        style={{
          left: `${outerPosition.x}px`,
          top: `${outerPosition.y}px`,
          transform: `translate(-50%, -50%) scale(${isPointer ? 2 : 1})`,
          opacity: isPointer ? 0.8 : 0.5,
        }}
      >
        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[hsl(var(--holographic-cyan))]/20 to-[hsl(var(--holographic-pink))]/20 blur-xl" />
      </div>
    </>
  );
};

export default CustomCursor;
